import React, { useEffect, useState } from 'react';
// Helpers
import {
  TASK_STATUSES,
  TASK_STATUS_LABELS,
  formatActiveTime,
} from 'constants/PreparationTasks';

/**
 * Status badge, live active time and the task buttons. Which buttons exist is the
 * server's call - canStart / canPause / canResume / canFinish - so nothing here
 * second-guesses the rules.
 */
const TaskActions = ({ task, isBusy, onStart, onPause, onResume, onFinish }) => {
  const [tick, setTick] = useState(0);
  const isRunning = task?.status === TASK_STATUSES.IN_PROGRESS;

  // The server sends the time at the moment of the response; keep counting from there.
  useEffect(() => {
    setTick(0);
    if (!isRunning) {
      return undefined;
    }
    const timer = setInterval(() => setTick((prev) => prev + 1), 1000);
    return () => clearInterval(timer);
  }, [isRunning, task?.activePreparationSeconds]);

  if (!task) {
    return null;
  }

  const hasAction = task.canStart || task.canPause || task.canResume || task.canFinish;
  const elapsed = (task.activePreparationSeconds ?? 0) + (isRunning ? tick : 0);
  const showTime = task.status !== TASK_STATUSES.NEW;
  // A paused task keeps Finish on screen, greyed out, so it is clear the task is not
  // closed - it just cannot be closed until it is resumed.
  const isPaused = task.status === TASK_STATUSES.PAUSED;

  return (
    <span className="task-state" onClick={(e) => e.stopPropagation()}>
      <span className={`task-status-badge status-${task.status}`}>
        {TASK_STATUS_LABELS[task.status] ?? task.status}
      </span>

      {showTime && <span className="task-time">{formatActiveTime(elapsed)}</span>}

      {task.canStart && (
        <button type="button" className="task-btn start" disabled={isBusy} onClick={onStart}>
          Start
        </button>
      )}
      {task.canResume && (
        <button type="button" className="task-btn start" disabled={isBusy} onClick={onResume}>
          Resume
        </button>
      )}
      {task.canPause && (
        <button type="button" className="task-btn pause" disabled={isBusy} onClick={onPause}>
          Pause
        </button>
      )}
      {(task.canFinish || isPaused) && (
        <button
          type="button"
          className="task-btn finish"
          disabled={isBusy || !task.canFinish}
          onClick={task.canFinish ? onFinish : undefined}
          title={task.canFinish ? undefined : 'Resume the task before finishing it'}
        >
          Finish
        </button>
      )}

      {!hasAction && task.actionDisabledReason && (
        <span className="task-hint">{task.actionDisabledReason}</span>
      )}
    </span>
  );
};

export default TaskActions;
